import React from "react";
import { FaEllipsisV } from "react-icons/fa";
import { useLinkTree } from "../../hooks/useLinkTree";
import { useConfirm } from "../../providers/ConfirmProvider";
import "../../styles/MyLinkTree.css";
import Dropdown from "../shared/Dropdown";
import FormField from "../shared/FormField";
import Button from "../shared/Button";
import { useLinkHubStats } from "../../hooks/useLinkHubStats";
import LinkHubLogo from "../../assets/images/LinkHub-Logo.svg";
import { useNotification } from "../../context/NotificationContext";
import QRCodeRenderer from "../shared/QRCodeRenderer";
import { QR_CONFIG } from "../../constants/qrCodeConstants";
import useQRCodeDownloader from "../../hooks/useQRCodeDownloader";
import DownloadIcon from "../../assets/icons/DownloadIcon";
import BaseModal from "../shared/BaseModal";

const CGI_LOGO_URL = "/images/CGI-Logo.jpg";

const MyLinkTree: React.FC = () => {
  const confirm = useConfirm();
  const {
    linkTree,
    items = [],
    isAddingLink,
    editingItem,
    newLinkTitle,
    newLinkUrl,
    linkTreeName,
    setIsAddingLink,
    setEditingItem,
    setNewLinkTitle,
    setNewLinkUrl,
    setLinkTreeName,
    handleAddLink,
    handleUpdateLink,
    handleDeleteLink,
    handleUpdateName,
    copyToClipboard,
    copyLinkHubLink,
    getShareLink,
  } = useLinkTree();

  const { stats } = useLinkHubStats(linkTree?.shortCode);
  const [showManageLinks, setShowManageLinks] = React.useState(false);
  const [showEditNameModal, setShowEditNameModal] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [openLinkDropdown, setOpenLinkDropdown] = React.useState<string | null>(
    null,
  );
  const threeDotButtonRef = React.useRef<HTMLButtonElement>(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = React.useState(false);
  const downloadDropdownRef = React.useRef<HTMLDivElement>(null);
  const linkDropdownRefs = React.useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});

  const { setNotification } = useNotification();

  const { handleDownloadQRCode } = useQRCodeDownloader(
    {
      [linkTree?.shortCode || ""]: {
        name: linkTree?.name || "LinkHub",
        shortCode: linkTree?.shortCode,
      },
    },
    false,
  );

  const shortLink = React.useMemo(
    () => getShareLink(linkTree?.shortCode),
    [getShareLink, linkTree?.shortCode],
  );

  const handleInlineAdd = React.useCallback(() => {
    if (!newLinkTitle?.trim()) {
      setNotification({ type: "error", message: "Please enter a link title" });
      return;
    }

    if (!newLinkUrl?.trim()) {
      setNotification({ type: "error", message: "Please enter a URL" });
      return;
    }

    handleAddLink();
    setNewLinkTitle("");
    setNewLinkUrl("");
    setIsAddingLink(false);
    setShowManageLinks(false);
  }, [
    handleAddLink,
    setNewLinkTitle,
    setNewLinkUrl,
    setIsAddingLink,
    setShowManageLinks,
    newLinkTitle,
    newLinkUrl,
    setNotification,
  ]);

  const handleDeleteWithConfirm = React.useCallback(
    async (linkId: string, linkTitle: string) => {
      const ok = await confirm({
        title: "Delete link",
        message: `Are you sure you want to delete "${linkTitle}"?`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      });

      if (ok) {
        handleDeleteLink(linkId);
      }
    },
    [confirm, handleDeleteLink],
  );

  const showInlineAdd = !linkTree || (items && items.length === 0);
  const hasLinks = linkTree && items && items.length > 0;

  const formatShortLink = (link: string) => {
    if (!link) return "";
    return link.replace(/^https?:\/\/(api\.)?/, "");
  };

  const handleEditNameClick = () => {
    setShowEditNameModal(true);
    setDropdownOpen(false);
  };

  const handleDownloadSelect = (format: "png" | "svg") => {
    handleDownloadQRCode(linkTree?.shortCode || "", format);
    setDownloadMenuOpen(false);
  };

  const handleManageLinksClick = () => {
    setShowManageLinks(true);
    setDropdownOpen(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openLinkDropdown) {
        const target = event.target as HTMLElement;
        if (
          !target.closest(".link-three-dot-btn") &&
          !target.closest(".link-dropdown")
        ) {
          setOpenLinkDropdown(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openLinkDropdown]);

  return (
    <div className="linktree-container">
      <div className="linktree-main-layout">
        {hasLinks && (
          <header className="linktree-header" role="banner">
            <div className="linktree-title-section">
              <div className="linktree-name-display">
                <h1>{linkTree?.name}</h1>
                <div style={{ position: "relative" }}>
                  <button
                    className="three-dot-btn"
                    ref={threeDotButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDropdownOpen(!dropdownOpen);
                    }}
                    aria-label="Open menu"
                  >
                    <FaEllipsisV size={22} color="#7D7C85" />
                  </button>

                  <Dropdown
                    open={dropdownOpen}
                    anchorRef={threeDotButtonRef}
                    className="three-dot-dropdown"
                    onClose={() => setDropdownOpen(false)}
                  >
                    <button
                      className="dropdown-item"
                      onClick={handleEditNameClick}
                    >
                      Edit Name
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={handleManageLinksClick}
                    >
                      Manage Links
                    </button>
                  </Dropdown>
                </div>
              </div>
            </div>

            <section
              className="linktree-share-section"
              aria-label="Share your hub"
            >
              <h3>Share Your LinkHub. Copy the Link or Download QR Code</h3>
              {shortLink ? (
                <div className="share-content-wrapper">
                  <div className="share-qr-section">
                    <QRCodeRenderer
                      id={`qr-code-${linkTree?.shortCode}`}
                      value={shortLink}
                      size={200}
                      color={QR_CONFIG.STYLE.DEFAULT_COLOR}
                      withLogo={true}
                      logoUrl={CGI_LOGO_URL}
                    />
                  </div>
                  <div className="share-info-section">
                    <div className="share-info-content">
                      <div className="share-info-lines">
                        <div className="short-link-line">
                          <span className="badge-title">Short Link:</span>
                          <span
                            className="short-link-text clickable"
                            onClick={() => copyToClipboard(shortLink)}
                            title="Click to copy"
                          >
                            {formatShortLink(shortLink)}
                          </span>
                        </div>
                        <div className="view-count-line">
                          <span className="badge-title">View Count:</span>
                          <span className="view-count-value">
                            {linkTree?.totalViews?.toLocaleString() || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="share-action-buttons">
                      <div
                        className="download-btn-wrapper"
                        ref={downloadDropdownRef}
                      >
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDownloadMenuOpen(!downloadMenuOpen);
                          }}
                          variant="secondary"
                          type="button"
                        >
                          Download
                          <span
                            className="download-svg-icon"
                            style={{ marginLeft: "6px" }}
                          >
                            <DownloadIcon />
                          </span>
                        </Button>
                        {downloadMenuOpen && (
                          <div className="download-dropdown-horizontal">
                            <button
                              className="download-option"
                              onClick={() => handleDownloadSelect("png")}
                            >
                              PNG
                            </button>
                            <button
                              className="download-option"
                              onClick={() => handleDownloadSelect("svg")}
                            >
                              SVG
                            </button>
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={copyLinkHubLink}
                        variant="primary"
                        type="button"
                      >
                        Copy Link
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <p>Loading...</p>
              )}
            </section>
          </header>
        )}

        {showInlineAdd && (
          <main className="linktree-content" aria-live="polite">
            <div className="linktree-empty">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                  marginBottom: "8px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <h3>Get started with your LinkHub</h3>
                  <p>
                    Add your first link to create your shareable LinkHub page.
                  </p>
                </div>

                <img
                  src={LinkHubLogo}
                  alt="LinkHub Logo"
                  style={{
                    width: "56px",
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              </div>

              <div
                className="add-inline-form"
                style={{
                  marginTop: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  width: "100%",
                }}
              >
                <FormField
                  label="Link Title"
                  type="text"
                  value={newLinkTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNewLinkTitle(e.target.value)
                  }
                  placeholder="e.g., LinkedIn Profile, Portfolio"
                  required
                />

                <FormField
                  label="URL"
                  type="url"
                  value={newLinkUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNewLinkUrl(e.target.value)
                  }
                  placeholder="https://example.com"
                  required
                />

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    width: "100%",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    onClick={handleInlineAdd}
                    variant="primary"
                    type="button"
                  >
                    Add Link
                  </Button>
                </div>
              </div>
            </div>
          </main>
        )}
      </div>

      {showManageLinks && (
        <BaseModal
          isOpen={showManageLinks}
          onClose={() => setShowManageLinks(false)}
          title="Manage Your LinkHub"
          width="490px"
          showCloseButton={true}
          maxHeight="85vh"
        >
          <div className="mlm-body">
            {!isAddingLink && (
              <Button
                onClick={() => setIsAddingLink(true)}
                variant="secondary"
                className="btn-add-new-link"
                type="button"
              >
                + Add New Link
              </Button>
            )}

            {isAddingLink && (
              <div className="add-link-form-modal">
                <h4>Add New Link</h4>
                <FormField
                  label="Link Title"
                  type="text"
                  value={newLinkTitle}
                  onChange={(e) => setNewLinkTitle(e.target.value)}
                  required
                />
                <FormField
                  label="URL"
                  type="text"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  required
                />
                <div className="form-actions-inline">
                  <Button
                    onClick={() => setIsAddingLink(false)}
                    variant="secondary"
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      handleAddLink();
                      setIsAddingLink(false);
                    }}
                    variant="primary"
                    disabled={!newLinkTitle?.trim() || !newLinkUrl?.trim()}
                    type="button"
                  >
                    Add Link
                  </Button>
                </div>
              </div>
            )}

            {items.length > 0 ? (
              <div className="existing-links-section">
                <h4>Your Links ({items.length})</h4>
                <div className="links-list">
                  {items.map((item) => (
                    <div key={item.id} className="link-item-modal">
                      {editingItem && editingItem.id === item.id ? (
                        <div className="link-edit-form">
                          <FormField
                            label="Title"
                            type="text"
                            value={editingItem.title || ""}
                            onChange={(e) =>
                              setEditingItem({
                                ...editingItem,
                                title: e.target.value,
                              })
                            }
                            required
                          />
                          <FormField
                            label="URL"
                            type="text"
                            value={editingItem.url || ""}
                            onChange={(e) =>
                              setEditingItem({
                                ...editingItem,
                                url: e.target.value,
                              })
                            }
                            placeholder="https://example.com"
                            required
                          />
                          <div className="form-actions-inline">
                            <Button
                              onClick={() => setEditingItem(null)}
                              variant="secondary"
                              type="button"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleUpdateLink(item.id || "")}
                              variant="primary"
                              type="button"
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="link-display">
                          <div className="manage-link-info">
                            <div className="manage-link-title">
                              {item.title}
                            </div>
                            <div className="manage-link-url">{item.url}</div>
                            <div className="manage-link-stats">
                              {(item.id && stats?.itemClickCounts?.[item.id]) ||
                                item.clickCount ||
                                0}{" "}
                              clicks
                            </div>
                          </div>
                          <div className="manage-link-actions">
                            <button
                              ref={(el) => {
                                linkDropdownRefs.current[item.id || ""] = el;
                              }}
                              className="link-three-dot-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenLinkDropdown(
                                  openLinkDropdown === item.id
                                    ? null
                                    : item.id || null,
                                );
                              }}
                              aria-label="Link options"
                            >
                              <FaEllipsisV size={16} color="#666" />
                            </button>

                            <Dropdown
                              open={openLinkDropdown === item.id}
                              anchorRef={{
                                current:
                                  linkDropdownRefs.current[item.id || ""] ||
                                  null,
                              }}
                              className="link-dropdown"
                              onClose={() => setOpenLinkDropdown(null)}
                            >
                              <button
                                className="link-dropdown-item"
                                onClick={(
                                  e: React.MouseEvent<HTMLButtonElement>,
                                ) => {
                                  e.stopPropagation();
                                  setEditingItem({ ...item });
                                  setOpenLinkDropdown(null);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="link-dropdown-item danger"
                                onClick={(
                                  e: React.MouseEvent<HTMLButtonElement>,
                                ) => {
                                  e.stopPropagation();
                                  handleDeleteWithConfirm(
                                    item.id || "",
                                    item.title || "this link",
                                  );
                                  setOpenLinkDropdown(null);
                                }}
                              >
                                Delete
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              !isAddingLink && (
                <div className="modal-empty-state">
                  <p>
                    No links yet. Click &quot;Add New Link&quot; to get started.
                  </p>
                </div>
              )
            )}
          </div>
        </BaseModal>
      )}

      {showEditNameModal && (
        <BaseModal
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          title="Edit LinkHub Name"
          width="420px"
          showCloseButton={true}
          footer={
            <>
              <Button
                onClick={() => setShowEditNameModal(false)}
                variant="secondary"
                type="button"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleUpdateName();
                  setShowEditNameModal(false);
                }}
                variant="primary"
                type="button"
              >
                Save
              </Button>
            </>
          }
        >
          <div className="mlm-body">
            <div className="add-link-form-modal">
              <FormField
                label="LinkHub Name"
                type="text"
                value={linkTreeName}
                onChange={(e) => setLinkTreeName(e.target.value)}
                placeholder="Enter LinkHub name"
                required
              />
            </div>
          </div>
        </BaseModal>
      )}
    </div>
  );
};

export default MyLinkTree;
